import SwipeUpModal from 'components/SwipeUpModal';
import { actionSetWorkOrderOpActionLoading } from 'lib/actions';
import useSelectActivity from 'lib/hooks/useSelectActivity';
import { getHasOpenJob } from 'lib/selectors/getHasOpenJob';
import useCreateActivitySetWithWorkOrder from 'pages/SelectJob/useCreateActivitySetWithWorkOrder';
import React, { useCallback, useMemo, useState } from 'react';
import { useJobDisplayName } from 'lib/hooks/useJobDisplayName';
import { useDispatch, useSelector } from 'react-redux';
import { getMachine } from 'lib/selectors/getMachine';
import { getCurrentWorkOrderOp } from 'lib/selectors/getCurrentWorkOrderOp';
import { useTranslation } from 'react-i18next';

import useChangeActivities from 'components/LaborTicket/hooks/useChangeActivities';
import { throwSuccessToast, throwErrorToast } from 'lib/utils/toast';
import useLookupActivityTypeName from 'lib/hooks/useLookupActivityTypeName';
import useRequestJobStartAt from 'lib/hooks/useRequestJobStartAt';
import { getPromptOperatorForPartCountMultiplier } from 'lib/selectors/getPromptOperatorForPartCountMultiplier';
import { getProductionActivityTypeRefs } from 'lib/selectors/getProductionActivityTypeRefs';
import { PartCountMultiplierDialogBase } from 'components/PartCountMultiplierDialog';
import {
  findMatchingActivities,
  isActivityDisabled,
} from 'lib/utils/activityMatching';
import { getSwitchActivityTypesOptions } from 'lib/selectors/getSwitchActivityTypesOptions';
import { getOpenActivityTypeRef } from 'lib/selectors/getOpenActivity';
import useBarcodeScanHandler from 'lib/hooks/useBarcodeScanHandler';
import { BARCODE_HANDLER_PRIORITIES } from 'lib/constants/barcodePriorities';
import { postMultiJobStartJobs } from 'lib/api/multiJobSelectRest';
import { Table } from './Table';

const resolvePartCountMultiplier = ({
  isPromptEnabled,
  activityTypeRef,
  productionActivityTypeRefs,
  partCountMultiplier,
  defaultPartCountMultiplier,
}) => {
  const isProductionActivity = productionActivityTypeRefs.includes(
    activityTypeRef
  );
  return isPromptEnabled && isProductionActivity
    ? partCountMultiplier
    : defaultPartCountMultiplier;
};

const SelectActivity = () => {
  const { t } = useTranslation();
  const {
    selectActivityOpen,
    selectActivityOptions,
    closeSelectActivity,
  } = useSelectActivity();

  const dispatch = useDispatch();
  const hasOpenJob = useSelector(getHasOpenJob);
  const machine = useSelector(getMachine);

  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const activityTypes = useSelector(getSwitchActivityTypesOptions);
  const openActivityTypeRef = useSelector(getOpenActivityTypeRef);

  const { workOrderOp, partOperation } = selectActivityOptions || {};

  const selectedWorkOrderOp = workOrderOp || currentWorkOrderOp;

  const isPromptEnabled = useSelector(getPromptOperatorForPartCountMultiplier);
  const productionActivityTypeRefs = useSelector(getProductionActivityTypeRefs);

  const defaultPartCountMultiplier =
    selectedWorkOrderOp?.quantityPerPart ||
    selectedWorkOrderOp?.quantityPerCycle ||
    partOperation?.quantityPerPart ||
    1;

  const [partCountMultiplier, setPartCountMultiplier] = useState(
    defaultPartCountMultiplier
  );
  const [
    isPartCountMultiplierModalOpen,
    setIsPartCountMultiplierModalOpen,
  ] = useState(false);

  const requestJobStartAt = useRequestJobStartAt();
  const requestCreateActivitySetWithWorkOrder = useCreateActivitySetWithWorkOrder(
    {
      machine,
      workOrderOp: selectedWorkOrderOp,
    }
  );
  const requestChangeActivities = useChangeActivities();
  const lookupActivityTypeName = useLookupActivityTypeName();
  const { getOrderOpDisplayName } = useJobDisplayName();

  // Memoize the order op display name to avoid re-rendering the component when the work order op changes
  const orderOpDisplayName = useMemo(() => {
    return getOrderOpDisplayName(selectedWorkOrderOp);
  }, [getOrderOpDisplayName, selectedWorkOrderOp]);

  const isApmStartJob = selectActivityOptions?.isApmStartJob;

  const handleActivityTypeSelected = useCallback(
    async (activityTypeRef, activityMode) => {
      if (isApmStartJob && selectedWorkOrderOp) {
        dispatch(actionSetWorkOrderOpActionLoading(true));
        try {
          await postMultiJobStartJobs(machine.machineRef, [
            {
              workOrderOperationRef: selectedWorkOrderOp.workOrderOperationRef,
              activityMode,
            },
          ]);
          throwSuccessToast(
            t('Started "{{workOrderId}}"', {
              workOrderId: selectedWorkOrderOp.workOrderId,
            })
          );
          window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Failed to start job:', err);
          throwErrorToast(t('Failed to start job'));
        } finally {
          dispatch(actionSetWorkOrderOpActionLoading(false));
        }
        return closeSelectActivity();
      }

      if (hasOpenJob && selectedWorkOrderOp) {
        dispatch(actionSetWorkOrderOpActionLoading(true));
        await requestChangeActivities({
          pendingActivityTypeRef: activityTypeRef,
        });

        const activityTypeName = lookupActivityTypeName(activityTypeRef);
        if (currentWorkOrderOp) {
          throwSuccessToast(t('Switched Activity:', { activityTypeName }));
        }
      } else {
        try {
          const startAt = await requestJobStartAt();

          const partCountMultiplierValue = resolvePartCountMultiplier({
            isPromptEnabled,
            activityTypeRef,
            productionActivityTypeRefs,
            partCountMultiplier,
            defaultPartCountMultiplier,
          });

          dispatch(actionSetWorkOrderOpActionLoading(true));

          await requestCreateActivitySetWithWorkOrder({
            activityTypeRef,
            partOperation,
            startAt,
            partCountMultiplier: partCountMultiplierValue,
          });
        } catch (error) {
          dispatch(actionSetWorkOrderOpActionLoading(false));
          if (error?.code !== 'USER_CANCELLED') {
            throwErrorToast(t('Failed to start activity'));
          }
          return null;
        }
      }

      dispatch(actionSetWorkOrderOpActionLoading(false));
      return closeSelectActivity();
    },
    [
      isApmStartJob,
      hasOpenJob,
      selectedWorkOrderOp,
      dispatch,
      machine,
      requestChangeActivities,
      lookupActivityTypeName,
      currentWorkOrderOp,
      t,
      requestJobStartAt,
      isPromptEnabled,
      productionActivityTypeRefs,
      partCountMultiplier,
      defaultPartCountMultiplier,
      requestCreateActivitySetWithWorkOrder,
      partOperation,
      closeSelectActivity,
    ]
  );

  const handleBarcodeScan = useCallback(
    (barcode) => {
      const matches = findMatchingActivities(barcode, activityTypes);

      if (matches.length === 0) {
        throwErrorToast('Error: Activity not found');
        return;
      }

      if (matches.length > 1) {
        throwErrorToast('Error: Multiple activities found');
        return;
      }

      const matchedActivity = matches[0];

      if (
        isActivityDisabled(matchedActivity.activityTypeRef, openActivityTypeRef)
      ) {
        throwErrorToast('Error: Activity already active');
        return;
      }

      throwSuccessToast(`Activity matched: ${matchedActivity.name}`);
      handleActivityTypeSelected(
        matchedActivity.activityTypeRef,
        matchedActivity.activityMode
      );
    },
    [activityTypes, openActivityTypeRef, handleActivityTypeSelected]
  );

  useBarcodeScanHandler('select-activity-modal', handleBarcodeScan, {
    priority: BARCODE_HANDLER_PRIORITIES.SELECT_ACTIVITY_MODAL,
    enabled: selectActivityOpen,
  });

  const headerText =
    selectedWorkOrderOp && selectedWorkOrderOp?.workOrder
      ? `${selectedWorkOrderOp.workOrderId} | ${orderOpDisplayName}`
      : t('Select Activity');

  return (
    <>
      <SwipeUpModal
        isOpen={selectActivityOpen}
        header={headerText}
        onClose={() => {
          return closeSelectActivity();
        }}
        details={
          <Table
            workOrderOp={selectedWorkOrderOp}
            onActivityTypeSelect={handleActivityTypeSelected}
            isApmStartJob={isApmStartJob}
            partCountMultiplier={partCountMultiplier}
            onEditPartCountMultiplier={() => {
              return setIsPartCountMultiplierModalOpen(true);
            }}
            isPromptEnabled={isPromptEnabled}
          />
        }
      />
      {isPartCountMultiplierModalOpen && (
        <PartCountMultiplierDialogBase
          defaultValue={partCountMultiplier}
          onSave={(newValue) => {
            setPartCountMultiplier(newValue);
            setIsPartCountMultiplierModalOpen(false);
          }}
          onCancel={() => {
            return setIsPartCountMultiplierModalOpen(false);
          }}
        />
      )}
    </>
  );
};

export { SelectActivity };
